import { EMPTY, Observable } from 'rxjs';
import {
  SlackMessageId,
  SlackMessagePermalink,
  SlackMessageSubType,
  SlackMessageText,
  SlackMessageTs,
  SlackMessageType
} from '../../value';
import { distinct, expand, filter, map, mergeMap, take, toArray } from 'rxjs/operators';

import { FirestoreQueryBuilder } from '../../../../../lib/gcp/builder/firestore-query.builder';
import { IFirestoreService } from '../../../../../lib/gcp/service/firestore.service';
import { ISlackMessageRepository } from '../../repository';
import { IsDeleted } from '../../../../kernel/base/value';
import { SlackChannelId } from '../../../slack-channel/value';
import { SlackMessage } from '../../entity';
import { SlackMessageNotFoundError } from '../../exception';
import { SlackUserId } from '../../../slack-user/value';
import { Timestamp } from '../../../../../utility/model/timestamp.value';

export class SlackMessageFirestoreRepository implements ISlackMessageRepository {
  private static readonly collectionId = 'slack_message';

  constructor(private readonly firestoreService: IFirestoreService) { }

  select(id: SlackMessageId): Observable<SlackMessage> {
    return this.firestoreService.getDocument(SlackMessageFirestoreRepository.collectionId, id).pipe(
      map(item => {
        if (!item || item && item.isDeleted === true) {
          throw new SlackMessageNotFoundError(`slack_message id「${id.value}」 is not found`);
        }
        return this.convertToEntity(item);
      })
    );
  }

  selectAll(builder: FirestoreQueryBuilder<SlackMessage>): Observable<SlackMessage> {
    return this.firestoreService.getCollection(SlackMessageFirestoreRepository.collectionId, builder).pipe(
      take(1),
      expand(items =>
        items.length
          ? this.firestoreService
            .getCollection(SlackMessageFirestoreRepository.collectionId, builder.startAfter(items[items.length - 1].id).limit(100))
            .pipe(take(1))
          : EMPTY
      ),
      mergeMap(items => items),
      distinct(item => item.id),
      filter(item => item.isDeleted !== true),
      map(item => this.convertToEntity(item))
    );
  }

  selectAllV1(builder: FirestoreQueryBuilder<SlackMessage>): Observable<SlackMessage> {
    return this.firestoreService.getCollection(SlackMessageFirestoreRepository.collectionId, builder).pipe(
      // take(1),
      // expand(items =>
      //   items.length
      //     ? this.firestoreService
      //       .getCollection(SlackMessageFirestoreRepository.collectionId, builder.startAfter(items[items.length - 1].id).limit(100))
      //       .pipe(take(1))
      //     : EMPTY
      // ),
      mergeMap(items => items),
      distinct(item => item.id),
      filter(item => item.isDeleted !== true),
      map(item => this.convertToEntity(item))
    );
  }

  insert(item: SlackMessage): Observable<SlackMessage> {
    item.createdAt = Timestamp.createByDate(new Date());
    item.updatedAt = Timestamp.createByDate(new Date());
    item.isDeleted = IsDeleted.create(false);
    return this.firestoreService.setDocument(SlackMessageFirestoreRepository.collectionId, this.convertToMap(item)).pipe(map(() => item));
  }

  update(message: SlackMessage): Observable<SlackMessage> {
    message.updatedAt = Timestamp.createByMillsec(+new Date());
    return this.firestoreService.getDocument(SlackMessageFirestoreRepository.collectionId, message.id).pipe(
      take(1),
      mergeMap(item => {
        if (!item || item && item.isDeleted === true) {
          throw new SlackMessageNotFoundError(`slack_message id「${message.id.value}」 is not found`);
        }
        return this.firestoreService.setDocument(SlackMessageFirestoreRepository.collectionId, this.convertToMap(message));
      }),
      map(_ => message)
    );
  }

  delete(id: SlackMessageId): Observable<void> {
    const proposal = new SlackMessage(id);
    proposal.isDeleted = IsDeleted.create(true);
    proposal.updatedAt = Timestamp.createByMillsec(+Date.now());
    return this.firestoreService.setDocument(SlackMessageFirestoreRepository.collectionId, this.convertToMap(proposal));
  }

  generateId(): SlackMessageId {
    return SlackMessageId.create(this.firestoreService.generateId());
  }

  private convertToMap(reaction: SlackMessage): object {
    return SlackMessage.allFields.reduce((p, key) => {
      if (reaction[key] === undefined) {
        return p;
      }
      const value = reaction[key] as { value: any };
      p[key] = value.value;
      return p;
    }, {});
  }

  private convertToEntity(item: any) {
    const slackMessage = new SlackMessage(SlackMessageId.create(item.id));
    slackMessage.channelId = SlackChannelId.create(item.channelId);
    slackMessage.userId = SlackUserId.create(item.userId);
    slackMessage.registeredBy = SlackUserId.create(item.registeredBy);
    slackMessage.ts = SlackMessageTs.create(item.ts);
    slackMessage.type = SlackMessageType.create(item.type);
    slackMessage.subtype = SlackMessageSubType.create(item.subtype);
    slackMessage.text = SlackMessageText.create(item.text);
    slackMessage.permalink = SlackMessagePermalink.create(item.permalink);
    slackMessage.createdAt = Timestamp.createByMillsec(item.createdAt.seconds * 1000);
    slackMessage.updatedAt = Timestamp.createByMillsec(item.updatedAt.seconds * 1000);
    slackMessage.isDeleted = item && item.isDeleted ? IsDeleted.create(item.isDeleted) : IsDeleted.create(false);
    return slackMessage;
  }
}
